/* eslint-disable import/no-unresolved */

'use client';

import { useEffect, useRef, useState } from 'react';
import {
  Pagination, Keyboard, A11y,
} from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/a11y';
import './quickTips.scss';
import PropTypes from 'prop-types';
import VideoCard from 'components/molecules/videoCard';
import { getInitialIndexSlide, focusSlideIntoView } from 'helpers/ui';
import DOMPurify from 'dompurify';

export default function QuickTips({ data }) {
  const swiperRef = useRef(null);
  const paginationRef = useRef(null);
  const [paginationReady, setPaginationReady] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const prevRef = useRef(null);
  const nextRef = useRef(null);

  const tipsData = (data?.elements || [])
    .filter((e) => e && e.enabled)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  useEffect(() => {
    if (paginationRef.current) setPaginationReady(true);
  }, []);

  useEffect(() => {
    const swiper = swiperRef.current;
    if (!swiper) return undefined;

    const handleSlideChange = () => {
      const realIndex = typeof swiper.realIndex === 'number' ? swiper.realIndex : 0;
      setActiveIndex(realIndex);
    };

    swiper.on('slideChange', handleSlideChange);
    handleSlideChange();

    return () => {
      swiper.off('slideChange', handleSlideChange);
    };
  }, []);

  const initialIndexSlide = getInitialIndexSlide(tipsData.length);

  return (
    <div className='tips' onFocus={(e) => focusSlideIntoView(swiperRef.current, e.target)}>
      <div className='quickTipsTitleContainer'>
        <div className='quickTipsTitleContainer__title proxima-nova-bold' dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(data.title) }} />
        <div className='quickTipsTitleContainer__subtitle' dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(data.body) }} />
      </div>
      {paginationReady && (
      <Swiper
        modules={[Pagination, Keyboard, A11y]}
        autoplay={false}
        initialSlide={initialIndexSlide}
        pagination={{ clickable: true }}
        keyboard={{ enabled: true, onlyInViewport: true }}
        onBeforeInit={(swiper) => {
          swiperRef.current = swiper;
          swiper.params.pagination.el = paginationRef.current;
          swiper.params.navigation.prevEl = prevRef.current;
          swiper.params.navigation.nextEl = nextRef.current;
        }}
        onAfterInit={(swiper) => {
          swiper.pagination.init();
          swiper.pagination.render();
          swiper.pagination.update();
          swiper.update();
        }}
        navigation={{
          prevEl: prevRef.current,
          nextEl: nextRef.current,
        }}
        slidesPerView='auto'
        centeredSlides
        onSlideChange={(swiper) => {
          setActiveIndex(swiper.realIndex);
        }}
      >
        {tipsData.map((element, idx) => (
          <SwiperSlide key={element.order ?? element.title ?? idx} className='tipsCarouselSlide'>
            <div className='tipsCardContainer'>
              <VideoCard
                element={element}
                isActive={idx === activeIndex}
                body={(
                  <div className='tipsDataContainer'>
                    <div
                      className='tipsDataContainer_title proxima-nova-bold'
                      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(element.title) }}
                    />
                    <div className='tipsDataContainer_body' dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(element.body) }} />
                  </div>
               )}
              />
            </div>
          </SwiperSlide>
        ))}
      </Swiper>
      )}
      <div className='promoPagination' ref={paginationRef} />
    </div>
  );
}

QuickTips.propTypes = {
  data: PropTypes.shape({
    title: PropTypes.string,
    body: PropTypes.string,
    elements: PropTypes.arrayOf(
      PropTypes.shape({
        enabled: PropTypes.bool,
        order: PropTypes.number,
        title: PropTypes.string,
        body: PropTypes.string,
        videoUrl: PropTypes.string,
        thumbnailUrl: PropTypes.string,
      }),
    ),
  }),
};
